import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';

import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subscription, first, take } from 'rxjs';

import { AnalyticsEvent, TrackingData } from '@hdms/shared/common-types';
import {
  AnalyticsService,
  BackdropService,
  BreakpointKey,
  BreakpointService,
  DEFAULT_NAVIGATION_DATA,
  OverlayWrapperService,
  WindowRefService
} from '@hdms/shared/util-core';

import { FloatingStackingService } from '../floating';

import { ModalAnalyticsService } from './modal-analytics.service';

let increment = 0;

export enum ModalSize {
  SM = 'sm',
  LG = 'lg'
}

export enum ModalDismissReason {
  BACKDROP_CLICK = 0,
  ESC = 1,
  CLOSE_EXTERNAL = 2,
  CLOSE_FOOTER = 3,
  CLOSE_HEADER = 4
}

@Component({
  selector: 'shrd-uic-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  /* Inject a new instance of FloatingStackingService in the modal */
  providers: [FloatingStackingService]
})
export class ModalComponent implements OnInit, OnDestroy {
  public readonly modalId = increment;
  public readonly closeOverlayLabel = 'Close|Overlay';
  public readonly openOverlayLabel = 'Open|Overlay';
  public scrollToTargetSelector: string;
  public isSmallModal: boolean;
  public isMobileView$: Observable<boolean>;

  private modalRef: NgbModalRef;
  private bpSubscription: Subscription;
  private backdropClass = 'modal-backdrop';
  private readonly url: string;

  @Input() subHeaderRef: TemplateRef<Element>;
  @Input() bodyRef: TemplateRef<Element>;
  @Input() footerRef: TemplateRef<Element>;
  @Input() modalLoadingOverlay: TemplateRef<Element>;
  @Input() customContentRef?: TemplateRef<Element>;
  @Input() showLoadingOverlay: boolean;
  @Input() closeText = 'Close';
  @Input() title: string;
  // TODO: as long Storybook doesn't support template literal types we've to comment it out and use the legacy way :/
  // @Input() size?: ModalSize | `${ModalSize}`;
  @Input() size?: ModalSize | 'sm' | 'lg';
  @Input() fitToContent?: boolean;
  @Input() centered = true;
  @Input() visibleMinBreakpoint?: BreakpointKey;
  @Input() visibleMaxBreakpoint?: BreakpointKey;
  @Input() closeDisabled: boolean;
  @Input() scrollbarEnabled = true;
  @Input() windowClass = '';
  @Input() pageViewData: TrackingData;
  @Input() analyticsEvent: AnalyticsEvent;
  @Input() set autoOpen(state: boolean) {
    state && this.open();
  }

  @Output() dismiss: EventEmitter<ModalDismissReason> = new EventEmitter();
  @Output() beforeDismiss: EventEmitter<void> = new EventEmitter();
  @Output() opened: EventEmitter<void> = new EventEmitter();

  @HostBinding('class.shrd-uic-modal') readonly hostClass = true;

  @ViewChild('content', { static: true }) content: TemplateRef<Element>;

  get hasHeader(): boolean {
    return !!this.title || this.isSmallModal;
  }

  get isOpen(): boolean {
    return !!this.modalRef;
  }

  constructor(
    private readonly modalService: NgbModal,
    private readonly breakpointService: BreakpointService,
    private readonly wrapperService: OverlayWrapperService,
    private readonly zone: NgZone,
    private readonly analyticsService: AnalyticsService,
    private readonly winRef: WindowRefService,
    private readonly modalAnalyticsService: ModalAnalyticsService,
    private readonly backdropService: BackdropService
  ) {
    increment++;
    this.url = this.winRef.nativeWindow.location.pathname;
  }

  ngOnInit(): void {
    this.size = this.size || ModalSize.LG;
    this.isSmallModal = this.size === ModalSize.SM;
    this.backdropClass = `${this.backdropClass} ${this.size}`;
    this.isMobileView$ = this.breakpointService.isMobileView$;
  }

  ngOnDestroy(): void {
    this.modalAnalyticsService.unregister(this);
  }

  open(scrollToTargetSelector?: string): void {
    this.scrollToTargetSelector = scrollToTargetSelector;
    this.isSmallModal = this.size === ModalSize.SM;
    const fullHeight = !this.isSmallModal && !this.fitToContent;
    this.modalAnalyticsService.registerModal(this);

    if (!this.modalRef) {
      this.backdropService.setPageScrollbarOffset(false, true);
      this.modalRef = this.modalService.open(this.customContentRef || this.content, {
        ariaLabelledBy: `shrd-uic-modal-title-${this.modalId}`,
        size: this.size || ModalSize.LG,
        centered: this.centered,
        container: `#${this.wrapperService.getWrapper().id}`,
        scrollable: true,
        windowClass: `${fullHeight ? 'h-100' : ''} ${this.windowClass}`,
        backdropClass: [this.backdropClass, this.size].join(' '),
        beforeDismiss: () => {
          if (!this.closeDisabled) {
            this.beforeDismiss.emit();
          }
          return !this.closeDisabled;
        }
      });

      /*
       * There is no callback in ngbModal when it has finished opening.
       * Therefore, as a workaround we wait until zone is stable again
       * (change detection has been applied) to manually emit `opened`.
       */
      this.zone.onMicrotaskEmpty.pipe(first()).subscribe(() => {
        this.opened.emit();
      });

      const dismissCb = (reason: ModalDismissReason): void => {
        // As of NgB v8, due to the introduction of animation the dismiss Promise
        // now is resolved/rejected before overlay is completely removed from DOM and
        // the included components OnDestroy will be invoked, so to keep same behavior
        // for existing logic depending on modal, we delay the final dismiss emission
        // until hidden observable emits.
        this.modalRef.hidden.pipe(take(1)).subscribe(() => {
          this.modalRef = null;
          this.dismiss.emit(reason);
          this.trackModalClose(reason);
        });

        if (this.bpSubscription && !this.bpSubscription.closed) {
          this.bpSubscription.unsubscribe();
        }
      };

      this.modalRef.result.then(dismissCb, dismissCb);

      this.subscribeToBreakpointChange();
    }

    this.trackModalOpen(this.pageViewData);
  }

  close(pageViewData?: TrackingData): void {
    this.closeDisabled = false;
    this.modalRef && this.modalRef.dismiss(ModalDismissReason.CLOSE_EXTERNAL);

    // invoke pageview from the previous modal on clicking back button
    if (pageViewData && this.analyticsEvent) {
      this.modalAnalyticsService.close(pageViewData, this);
      if (pageViewData.navigationItemDetails === 'Cancel' || pageViewData.navigationItemDetails === 'Back') {
        this.sendEvent(this.pageViewData.pageName, `${pageViewData.navigationItemDetails}|Overlay`);
      } else {
        this.sendEvent(this.pageViewData.pageName, this.closeOverlayLabel);
      }
    }
  }

  dismissAll(): void {
    // only close all underlying modals too, if the frontmost (this one) is fully large sized
    if (this.size === ModalSize.LG && !this.fitToContent) {
      this.modalService.dismissAll(ModalDismissReason.CLOSE_HEADER);
    } else {
      this.modalRef.dismiss(ModalDismissReason.CLOSE_HEADER);
    }
    this.trackDismissAll();
  }

  trackModalOpen(pageViewData: TrackingData): void {
    if (pageViewData && this.analyticsEvent) {
      !this.isSmallModal && this.trackPageView(pageViewData);
      this.sendEvent(pageViewData.pageName, this.openOverlayLabel);
    }
  }

  trackModalClose(reason: number): void {
    if (!this.isSmallModal && [0, 1].includes(reason) && this.analyticsEvent) {
      this.modalAnalyticsService.close(DEFAULT_NAVIGATION_DATA, this);
      this.sendEvent(this.pageViewData.pageName, this.closeOverlayLabel);
    }
  }

  trackDismissAll(): void {
    if (this.analyticsEvent) {
      !this.isSmallModal && this.analyticsService.trackPageView(this.url, DEFAULT_NAVIGATION_DATA, true);
      this.sendEvent(this.pageViewData.pageName, this.closeOverlayLabel);
    }
  }

  // Track Page View
  trackPageView(pageViewData: TrackingData): void {
    const modalPageViewData = {
      ...pageViewData,
      templateType: 'Large Modal Page'
    };
    this.analyticsService.trackPageView(this.url, modalPageViewData, true);
  }

  // Track Analytics Event
  sendEvent(pageName: string, action: string, omitInitialTrackingData = true): void {
    this.analyticsService.sendEvent(
      { ...this.analyticsEvent, eventLabel: `${pageName.replace(/\s/g, '')}|${action}` },
      omitInitialTrackingData
    );
  }

  public get scrollContainer(): HTMLElement {
    return this.scrollbarEnabled
      ? (this.modalRef as any)?._windowCmptRef.location.nativeElement.querySelector('.js-scroll-container')
      : null;
  }

  private subscribeToBreakpointChange(): void {
    if (this.visibleMinBreakpoint || this.visibleMaxBreakpoint) {
      this.bpSubscription = this.breakpointService.breakpointChange$.subscribe(() => {
        if (!this.breakpointService.isWithinBreakpoints(this.visibleMinBreakpoint, this.visibleMaxBreakpoint)) {
          this.close();
        }
      });
    }
  }
}
